import { db } from "firebaseConfig";
import { realEstateContactConverter } from "utils/firestoreConverters";
import { CONTACT_DOCUMENT_SOURCE } from "hooks/contacts/contactDocumentSource";
import { postProcessContact } from "./helper";
import type { RealEstateContact, REST_schema } from "../../@types/sharedSchema";

/** `POST /contacts/get`, as `useWebotServer()` hands it out (`contactsGet`). */
export type ContactGetter = (
  request: REST_schema.CrmContactGetRequest
) => Promise<REST_schema.CrmContactGetResponse>;

/**
 * What one read asks for: a document id, or every spelling a phone might be filed under, in
 * order of preference (`getPhoneCandidates`) — the first that exists answers.
 */
export type ContactOnceQuery = { contactId: string } | { candidates: readonly string[] };

/**
 * ONE contact, read ONCE, from where THIS BROWSER reads contacts (`CONTACT_DOCUMENT_SOURCE`).
 *
 * For the moment a screen holds a phone and needs the contact itself, once: a picker handing
 * its pick to a form, a page opened with `?cn=`, a save deciding between creating and editing.
 * A card that STAYS on screen wants the live hook instead (`useContactByPhone` /
 * `useLiveContact`), which keeps the contact fresh under edits made elsewhere.
 *
 * In the new way (`'server'`) the answer is the server's (`POST /contacts/get`, the caller's
 * own book — for a member of a shared office, the office's record on their list). Otherwise it
 * is the caller's Firestore document, exactly as these screens read it until 2026-10-22. The
 * difference matters only for a shared office, which runs the new way: there the Firestore
 * document is the member's frozen copy from before the merge, or nothing — and three of these
 * screens could save that copy's old values over the office's record (the new-contact form's
 * "edit instead", "edit before matching", a listing's seller).
 *
 * Resolves to `null` when there is no such contact; REJECTS when the read itself fails,
 * because "could not read" is not "does not exist". The document is returned as stored (not
 * post-processed; see `readContactDocument`).
 */
export const readContactOnce = async (
  contactsGet: ContactGetter,
  userEmail: string,
  query: ContactOnceQuery
): Promise<RealEstateContact | null> => {
  if (CONTACT_DOCUMENT_SOURCE === "server") {
    const response = await contactsGet(
      "contactId" in query ? { contactId: query.contactId } : { candidates: [...query.candidates] }
    );
    if (!response.found || !response.doc) return null;
    // The id is the identity every receiver reads (`contact.id`): the one the server found.
    const id = response.contactId ?? ("contactId" in query ? query.contactId : undefined);
    return { ...response.doc, ...(id ? { id } : {}) };
  }

  const contacts = db
    .collection("dashboard-users-v2")
    .doc(userEmail)
    .collection("contacts")
    .withConverter(realEstateContactConverter);
  if ("contactId" in query) {
    const snapshot = await contacts.doc(query.contactId).get();
    return snapshot.exists ? (snapshot.data() as RealEstateContact) : null;
  }
  if (!query.candidates.length) return null;
  // Firestore's `in` takes at most 10 values.
  const snapshot = await contacts.where("__name__", "in", query.candidates.slice(0, 10)).limit(1).get();
  return snapshot.empty ? null : (snapshot.docs[0].data() as RealEstateContact);
};

/**
 * The same read, POST-PROCESSED like every other contact the app holds — for a picker handing
 * its pick to a form: the options a picker lists are looked-up SUMMARIES (a name, a phone, a
 * 🔥), but the form that receives the pick prefills from the whole contact (an email, an ID
 * number, a budget range), so the document is read at the moment of the pick.
 */
export const readContactDocument = async (
  contactsGet: ContactGetter,
  userEmail: string,
  contactId: string
): Promise<RealEstateContact | null> => {
  const contact = await readContactOnce(contactsGet, userEmail, { contactId });
  return contact ? postProcessContact({ ...contact }, userEmail) : null;
};
